// src/pages/ProductManagement.jsx
import React, { useState, useEffect } from 'react';
import AdminProductForm from './AdminProductForm'; 
import api from '../services/api'; // Or wherever your Axios configuration instance is stored

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null); // null = Add Mode, object = Edit Mode
  const [isLoading, setIsLoading] = useState(false);

  // 1. READ: Fetch all items on component load
  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      // Calling your public endpoint or an admin list endpoint
      const response = await api.get('/products'); 
      setProducts(response.data);
    } catch (error) {
      console.error("Failed to load inventory:", error);
      alert("Error loading catalog registry.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // 2. PREPARE UPDATE: Populate form with selected row details
  const handleEditInit = (product) => {
    setEditingProduct(product);
    setShowForm(true);
  };

  // 3. DELETE: Trigger soft delete endpoint execution
  const handleDelete = async (id) => {
  if (window.confirm("Are you sure you want to flag this item as inactive?")) {
    try {
      // ⭐ Fixed: Removed the duplicate /api prefix
      await api.delete(`/admin/products/${id}`);
      
      alert("Product successfully deleted.");
      // Instantly clean up UI state matching array filter
      setProducts(products.filter((p) => p.id !== id));
    } catch (error) {
      console.error("Deletion failed:", error);
      alert(error.response?.data?.message || "Failed to execute delete statement.");
    }
  }
};

  // Helper to refresh table view and reset mode flags after successful form submission
  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingProduct(null);
    fetchProducts();
  };

  return (
    <div style={styles.container}>
      {/* Header Block */}
      <div style={styles.header}>
        <div>
          <button style={styles.backBtn} onClick={() => window.location.href='/admin/dashboard'}>
            ← Back to Dashboard
          </button>
          <h1 style={styles.title}>Products & Inventory Registry</h1>
        </div>
        <button 
          style={showForm ? styles.cancelBtn : styles.addBtn} 
          onClick={() => {
            setShowForm(!showForm);
            if (showForm) setEditingProduct(null); // Reset edit context if canceling
          }}
        >
          {showForm ? 'Cancel Operation' : '+ Upload New Merchandise'}
        </button>
      </div>

      <hr style={styles.divider} />

      {/* Form View Mode (Handles both Create and Update) */}
      {showForm ? (
        <div>
          <h2 style={styles.sectionSubtitle}>
            {editingProduct ? `Modifying: ${editingProduct.name}` : 'Register New Merchandise Entry'}
          </h2>
          {/* Pass editingProduct into your form. 
             Inside AdminProductForm, if initialData exists, populate state fields 
             and call PUT `/api/admin/products/${initialData.id}` instead of POST.
          */}
          <AdminProductForm 
            initialData={editingProduct} 
            onSuccess={handleFormSuccess} 
          />
        </div>
      ) : (
        /* Data Grid Listing Mode */
        <div>
          {isLoading ? (
            <div style={styles.placeholderState}><h3>Loading inventory records...</h3></div>
          ) : products.length === 0 ? (
            <div style={styles.placeholderState}>
              <div style={styles.icon}>👕</div>
              <h3>No Product Display Selected</h3>
              <p>Click the <strong>"+ Upload New Merchandise"</strong> button above to populate your database.</p>
            </div>
          ) : (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.thRow}>
                    <th style={styles.th}>ID</th>
                    <th style={styles.th}>Product Details</th>
                    <th style={styles.th}>Category</th>
                    <th style={styles.th, {textAlign: 'center'}}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product.id} style={styles.tr}>
                      <td style={styles.td}>{product.id}</td>
                      <td style={styles.td}>
                        <strong>{product.name}</strong>
                        <div style={{fontSize: '0.8rem', color: '#718096'}}>{product.description?.substring(0, 50)}...</div>
                      </td>
                      <td style={styles.td}><span style={styles.badge}>{product.category}</span></td>
                    
                      <td style={{...styles.td, textAlign: 'center'}}>
                        <button 
                          style={styles.editBtn} 
                          onClick={() => handleEditInit(product)}
                        >
                          Edit
                        </button>
                        <button 
                          style={styles.deleteBtn} 
                          onClick={() => handleDelete(product.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { padding: '40px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'Segoe UI, sans-serif' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' },
  backBtn: { background: 'none', border: 'none', color: '#2b6cb0', cursor: 'pointer', padding: 0, fontSize: '0.9rem', marginBottom: '10px', textDecoration: 'underline' },
  title: { margin: 0, fontSize: '1.8rem', color: '#111' },
  sectionSubtitle: { fontSize: '1.2rem', marginBottom: '20px', color: '#4a5568' },
  addBtn: { backgroundColor: '#2b6cb0', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' },
  cancelBtn: { backgroundColor: '#718096', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' },
  divider: { border: '0', height: '1px', background: '#e0e0e0', margin: '20px 0 30px' },
  placeholderState: { textAlign: 'center', padding: '60px 20px', border: '2px dashed #e2e8f0', borderRadius: '12px', background: '#f7fafc', color: '#718096' },
  icon: { fontSize: '3.5rem', marginBottom: '15px' },
  
  // Table Design Language Modifications
  tableWrapper: { overflowX: 'auto', background: '#fff', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' },
  table: { width: '100%', borderCollapse: 'collapse', textAlign: 'left' },
  thRow: { background: '#f7fafc', borderBottom: '2px solid #edf2f7' },
  th: { padding: '16px', color: '#4a5568', fontWeight: '600', fontSize: '0.9rem' },
  tr: { borderBottom: '1px solid #edf2f7', transition: 'background 0.2s' },
  td: { padding: '16px', color: '#2d3748', verticalAlign: 'middle', fontSize: '0.95rem' },
  badge: { background: '#e2e8f0', padding: '4px 8px', borderRadius: '4px', fontSize: '0.8rem', fontWeight: '600' },
  editBtn: { background: '#ecc94b', color: '#744210', border: 'none', padding: '6px 12px', marginRight: '8px', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' },
  deleteBtn: { background: '#fed7d7', color: '#9b2c2c', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }
};

export default ProductManagement;